import React, { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Sparkles, Send, Loader2, Mail } from 'lucide-react';
import { STAGES, STAGE_LABELS } from '@/lib/format';

export default function AIEmailPanel({
  candidate,      // optional
  onboarding,     // optional
  companyId,
  entityType = 'candidate',
  entityId,
  defaultKind = 'recruitment_stage',
  templates = [],
  onSent,
}) {
  const [stage, setStage] = useState(candidate?.stage || onboarding?.stage || 'screening');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [suggestedCc, setSuggestedCc] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [smtp, setSmtp] = useState({ configured: false });

  useEffect(() => {
    setTo(candidate?.email || onboarding?.email || '');
  }, [candidate?.email, onboarding?.email]);

  useEffect(() => {
    api.get('/emails/smtp-status').then((r) => setSmtp(r.data)).catch(() => {});
  }, []);

  const applyTemplate = (tid) => {
    const t = templates.find((x) => x.id === tid);
    if (!t) return;
    setSelectedTemplate(tid);
    // Simple placeholder replacement
    const name = candidate?.name || onboarding?.name || '';
    const role = candidate?.role_applied || onboarding?.role || '';
    const cn = candidate?.company_name || onboarding?.company_name || '';
    setSubject(
      (t.subject || '')
        .replaceAll('{{candidate_name}}', name)
        .replaceAll('{{role}}', role)
        .replaceAll('{{company_name}}', cn)
    );
    setBody(
      (t.body || '')
        .replaceAll('{{candidate_name}}', name)
        .replaceAll('{{role}}', role)
        .replaceAll('{{company_name}}', cn)
        .replaceAll('{{recruiter_name}}', 'HR Compass Team')
        .replaceAll('{{deadline}}', 'next Friday')
    );
  };

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await api.post('/ai/draft-email', {
        candidate_id: candidate?.id,
        onboarding_id: onboarding?.id,
        company_id: companyId,
        stage,
        kind: defaultKind,
      });
      setSubject(r.data.subject);
      setBody(r.data.body);
      setSuggestedCc(r.data.suggested_cc || []);
      if (!to && r.data.to && r.data.to.length) setTo(r.data.to.join(', '));
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI generation failed');
    } finally { setGenerating(false); }
  };

  const send = async () => {
    const toList = to.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    const ccList = cc.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
    if (!toList.length) return toast.error('At least one recipient (To) is required');
    if (!subject.trim()) return toast.error('Subject is required');
    setSending(true);
    try {
      const r = await api.post('/emails/send', {
        to: toList, cc: ccList, subject, body, company_id: companyId, entity_type: entityType, entity_id: entityId,
      });
      const status = r.data.result?.status;
      if (status === 'sent') toast.success('Email sent');
      else if (status === 'queued') toast.message('SMTP not configured - captured as draft.', { description: 'Emails will be delivered once you configure SMTP in Admin → Settings.' });
      else toast.error('Send failed: ' + (r.data.result?.reason || 'unknown'));
      onSent && onSent(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Send failed');
    } finally { setSending(false); }
  };

  const applySuggestedCc = () => {
    if (!suggestedCc.length) return;
    setCc(suggestedCc.join(', '));
  };

  const relevantTemplates = templates.filter((t) => {
    if (entityType === 'candidate') return t.category === 'recruitment';
    if (entityType === 'onboarding') return t.category === 'onboarding';
    return true;
  });

  return (
    <div className="rounded-xl border bg-card p-4 space-y-3" data-testid="ai-email-panel">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-semibold flex items-center gap-2"><Mail className="h-4 w-4 text-primary" /> Email draft</h3>
        {!smtp.configured && <span className="text-[10px] uppercase tracking-wider text-[hsl(var(--warn))] bg-[hsl(var(--warn-soft))] px-2 py-0.5 rounded">SMTP not configured</span>}
      </div>

      <div className="grid grid-cols-2 gap-2">
        {entityType === 'candidate' && (
          <div className="space-y-1">
            <Label className="text-xs">Stage template</Label>
            <Select value={stage} onValueChange={setStage}>
              <SelectTrigger data-testid="email-stage-select"><SelectValue placeholder="Stage" /></SelectTrigger>
              <SelectContent>{STAGES.map((s) => <SelectItem key={s} value={s}>{STAGE_LABELS[s]}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <Label className="text-xs">Saved templates</Label>
          <Select value={selectedTemplate} onValueChange={applyTemplate}>
            <SelectTrigger data-testid="email-template-select"><SelectValue placeholder="Pick a template" /></SelectTrigger>
            <SelectContent>{relevantTemplates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={generate} disabled={generating} data-testid="ai-email-generate-button">
          {generating ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} AI draft
        </Button>
      </div>

      <div className="grid gap-2">
        <div className="space-y-1"><Label className="text-xs">To</Label><Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="candidate@example.com" data-testid="email-to-input" /></div>
        <div className="space-y-1">
          <div className="flex items-center justify-between"><Label className="text-xs">CC</Label>{suggestedCc.length > 0 && <button onClick={applySuggestedCc} className="text-xs text-primary hover:underline" data-testid="email-apply-suggested-cc">Add {suggestedCc.length} suggested</button>}</div>
          <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="director@company.com" data-testid="email-cc-input" />
          {suggestedCc.length > 0 && <p className="text-[10px] text-muted-foreground">Suggested from company: {suggestedCc.join(', ')}</p>}
        </div>
        <div className="space-y-1"><Label className="text-xs">Subject</Label><Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="email-subject-input" /></div>
        <div className="space-y-1"><Label className="text-xs">Body</Label><Textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} data-testid="email-body-input" /></div>
      </div>

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="outline" onClick={async () => {
          const name = prompt('Template name?', `${defaultKind === 'recruitment_stage' ? stage : entityType} · ${new Date().toLocaleDateString()}`);
          if (!name) return;
          try {
            await api.post('/ai/save-as-template', {
              company_id: companyId,
              name,
              category: entityType === 'candidate' ? 'recruitment' : entityType,
              stage: entityType === 'candidate' ? stage : null,
              subject, body,
            });
            toast.success('Saved as template');
          } catch (e) { toast.error('Failed to save template'); }
        }} disabled={!subject || !body} data-testid="email-save-template">Save as template</Button>
        <Button size="sm" onClick={send} disabled={sending || !subject || !body} data-testid="email-send-button">
          {sending ? <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> : <Send className="h-3.5 w-3.5 mr-1" />} Send
        </Button>
      </div>
    </div>
  );
}
